// src/pages/login/index.jsx
import React, { useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { setAccessToken } from "../../lib/api"; // axios + token store (access in memorie)
import { useAuth } from "../../auth/AuthProvider";

// Ruta unde vrem să ajungă angajatul pur
const EMPLOYEE_CALENDAR_PATH = "/app/appointment-calendar";

const isIp = (hostname) => /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname);

// scoate slug-ul din host (prod / subdomain.localhost). Pe localhost simplu => null.
function getSlugFromHost() {
  try {
    const hostname = window.location.hostname; // fără port
    if (!hostname) return null;

    if (hostname.endsWith(".localhost")) {
      // dev: <slug>.localhost
      return hostname.split(".")[0] || null;
    }

    if (hostname === "localhost" || hostname.startsWith("127.") || isIp(hostname)) {
      return null;
    }

    // prod: <slug>.<domain>.<tld>
    const parts = hostname.split(".");
    if (parts.length >= 3) return parts[0];
  } catch (e) {}

  return null;
}

// Copiem aceeași logică de alegere a business-ului ca în AuthProvider
function pickInitialBusinessId(bizList) {
  if (!bizList || bizList.length === 0) return null;

  // 1) Potrivim subdomeniul:
  //   prod:  afacere-test1.appointiq.ro   -> slug = afacere-test1
  //   dev:   test-nou.localhost           -> slug = test-nou
  try {
    const hostname = window.location.hostname; // fără port
    let slugFromHost = null;

    if (hostname && hostname.endsWith(".localhost")) {
      // dev: <slug>.localhost
      slugFromHost = hostname.split(".")[0];
    } else if (hostname && hostname !== "localhost" && !hostname.startsWith("127.") && !isIp(hostname)) {
      // prod: <slug>.<domain>.<tld>
      const parts = hostname.split(".");
      if (parts.length >= 3) slugFromHost = parts[0];
    }

    if (slugFromHost) {
      const match = bizList.find(
        (b) =>
          String(b.slug || "").toLowerCase() === String(slugFromHost).toLowerCase()
      );
      if (match) return String(match.id);

      // IMPORTANT: dacă ești pe un subdomeniu, nu facem fallback la alt business din storage,
      // ca să nu "sari" între afaceri.
      return null;
    }
  } catch (e) {
    // în caz de SSR / lipsă window, ignorăm
  }

  // 2) Dacă aveam deja un business salvat și încă există, îl păstrăm
  const stored = localStorage.getItem("current_business_id");
  if (stored && bizList.some((b) => String(b.id) === stored)) {
    return stored;
  }

  // 3) fallback: primul business
  return String(bizList[0].id);
}

// Verifică dacă, pentru business-ul curent, userul e "angajat pur"
function isPureEmployeeForBusiness(me, businessId) {
  if (!me || !businessId) return false;

  const user = me.user || me;
  const roles = me.roles || [];
  const roleDetails = me.roleDetails || user.role_details || null;

  const isSuperAdmin = roles.includes("Super Administrator");

  const businessRoles = roleDetails?.business_roles || [];
  const locationRoles = roleDetails?.location_roles || [];

  const currentBizId = String(businessId);

  const isOwnerCurrentBusiness =
    isSuperAdmin ||
    businessRoles.some(
      (br) => String(br.business_id) === currentBizId && br.role === "owner"
    );

  const locationRolesCurrentBusiness = locationRoles.filter(
    (lr) => String(lr.business_id) === currentBizId
  );

  const isDirectorCurrentBusiness = locationRolesCurrentBusiness.some(
    (lr) => lr.role === "director"
  );
  const isReceptionCurrentBusiness = locationRolesCurrentBusiness.some(
    (lr) => lr.role === "reception"
  );
  const isEmployeeCurrentBusiness = locationRolesCurrentBusiness.some(
    (lr) => lr.role === "employee"
  );

  const isPureEmployee =
    !isSuperAdmin &&
    !isOwnerCurrentBusiness &&
    !isDirectorCurrentBusiness &&
    !isReceptionCurrentBusiness &&
    isEmployeeCurrentBusiness;

  return isPureEmployee;
}

// Decide unde îl ducem pe user după login
function decideRedirect({ me, returnTo }) {
  // Dacă venea de pe o rută protejată, îl trimitem înapoi acolo
  if (returnTo) return returnTo;

  if (!me) return "/app";

  const businesses = me.businesses || [];
  if (!businesses.length) return "/app";

  const chosenBizId = pickInitialBusinessId(businesses);
  if (!chosenBizId) {
    // dacă ești pe un subdomeniu care nu corespunde niciunui business din /auth/me,
    // nu încercăm să ghicim alt business (evităm cross-business).
    return "/app";
  }

  const pureEmployee = isPureEmployeeForBusiness(me, chosenBizId);

  if (pureEmployee) {
    // angajat pur → direct în calendarul lui
    return EMPLOYEE_CALENDAR_PATH;
  }

  // restul rolurilor merg în dashboard-ul implicit
  return "/app";
}

export default function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { loginSuccess, returnTo } = useAuth();

  // ✅ pe localhost nu avem subdomeniu → cerem slug manual
  const hostSlug = useMemo(() => getSlugFromHost(), []);
  const needsManualSlug = useMemo(() => !hostSlug, [hostSlug]);
  const [businessSlug, setBusinessSlug] = useState(
    () => localStorage.getItem("current_business_slug") || ""
  );

  const effectiveSlug = useMemo(() => {
    const s = (hostSlug || businessSlug || "").trim();
    return s || "";
  }, [hostSlug, businessSlug]);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);

    const extractMsg = (err) =>
      err?.response?.data?.detail ||
      err?.response?.data?.error ||
      err?.response?.data?.message ||
      err?.message ||
      t("login.authFailed");

    try {
      // ✅ fără slug (pe localhost) backend-ul zice "Missing business context"
      if (!effectiveSlug) {
        setErr(t("login.missingSlug"));
        setLoading(false);
        return;
      }

      const bizHeaders = { "X-Business-Subdomain": effectiveSlug };

      // 1) încearcă întâi cu email (dacă backend-ul suportă)
      let authData;
      try {
        const r1 = await api.post("/auth/login", { email, password, business_slug: effectiveSlug }, { headers: bizHeaders });
        authData = r1.data;
      } catch (err1) {
        // 2) fallback: username
        const r2 = await api.post(
          "/auth/login",
          { username: email, password, business_slug: effectiveSlug },
          { headers: bizHeaders }
        );
        authData = r2.data;
      }

      const access = authData?.access || authData?.access_token;
      if (!access) throw new Error("Răspuns invalid: lipsește access token.");

      // 3) access în memorie (refresh e în cookie HttpOnly setat de backend)
      setAccessToken(access);

      // 4) /auth/me pentru profil + roluri + business-uri
      const { data: me } = await api.get("/auth/me", { headers: bizHeaders });

      // ✅ persistă business context util pentru localhost (și pentru interceptoare)
      const businesses = me?.businesses || [];
      const chosenBizId = pickInitialBusinessId(businesses);

      if (chosenBizId) {
        const chosenBiz = businesses.find((b) => String(b.id) === String(chosenBizId));
        localStorage.setItem("current_business_id", String(chosenBizId));
        if (chosenBiz?.slug) localStorage.setItem("current_business_slug", String(chosenBiz.slug));
        else localStorage.setItem("current_business_slug", effectiveSlug);
      } else {
        // dacă nu putem alege din me.businesses (ex: mismatch subdomain), măcar păstrăm slug-ul pentru headers
        localStorage.setItem("current_business_slug", effectiveSlug);
      }

      // 5) actualizează contextul (AuthProvider)
      loginSuccess({ access, me });

      // 6) decidem unde îl trimitem
      const destination = decideRedirect({ me, returnTo });
      navigate(destination, { replace: true });
    } catch (e) {
      console.error(e);
      setErr(extractMsg(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-md bg-white border rounded-xl p-6 shadow-sm">
        <h1 className="text-xl font-semibold mb-1">{t("login.title")}</h1>
        <p className="text-sm text-gray-500 mb-4">{t("login.subtitle")}</p>

        {err && (
          <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {err}
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-3">
          {/* ✅ doar pe localhost / IP */}
          {needsManualSlug && (
            <div>
              <label className="text-sm text-gray-700">{t("login.businessSlug")}</label>
              <input
                type="text"
                value={businessSlug}
                onChange={(e) => setBusinessSlug(e.target.value)}
                className="mt-1 w-full rounded-md border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="ex: afacerea-test"
                required
              />
              <p className="mt-1 text-xs text-gray-500">
                {t("login.slugHint")}
              </p>
            </div>
          )}

          <div>
            <label className="text-sm text-gray-700">{t("login.emailOrUsername")}</label>
            <input
              type="text"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full rounded-md border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-500"
              autoFocus
              required
            />
          </div>

          <div>
            <label className="text-sm text-gray-700">{t("login.password")}</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full rounded-md border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-500"
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-md bg-emerald-600 text-white py-2 text-sm font-medium hover:bg-emerald-700 disabled:opacity-60"
          >
            {loading ? t("login.submitting") : t("login.submit")}
          </button>
        </form>

        <div className="mt-3 text-right">
          <Link to="/app/forgot-password" className="text-sm text-emerald-700 hover:underline">
            {t("login.forgotPassword")}
          </Link>
        </div>
      </div>
    </div>
  );
}
